import {
  OPERATIONAL_MODULES,
  OPERATIONAL_PROFILES,
  SECURITY_ROLES,
  type OperationalModule,
  type OperationalProfile,
  type SecurityRole,
} from '../../authorization/model.js';

export interface AuthorizationProfile {
  subjectId: string;
  tenantId: string;
  roles: readonly SecurityRole[];
  role: SecurityRole;
  operationalProfile: OperationalProfile;
  preferredLandingRoute: '/overview' | '/backups' | '/3cx' | '/onboarding';
  defaultRoute: '/overview' | '/backups';
  dashboardOrder: readonly OperationalModule[];
}

export interface AuthorizationProfileProvider {
  load(identityToken: string): Promise<AuthorizationProfile>;
}

export class LocalAuthorizationProfileProvider implements AuthorizationProfileProvider {
  async load(identityToken: string): Promise<AuthorizationProfile> {
    if (identityToken.length === 0) throw new Error('Authenticated session is unavailable');
    const response = await fetch('/api/profile', {
      method: 'GET',
      headers: { Authorization: `Bearer ${identityToken}` },
      cache: 'no-store',
    });
    if (!response.ok) throw new Error('Authorization profile is unavailable');
    const payload = await response.json() as unknown;
    if (!isAuthorizationProfile(payload)) throw new Error('Authorization profile is invalid');
    return payload;
  }
}

function isAuthorizationProfile(value: unknown): value is AuthorizationProfile {
  if (!isRecord(value)) return false;
  return typeof value.subjectId === 'string'
    && typeof value.tenantId === 'string'
    && Array.isArray(value.roles)
    && value.roles.every((role) => (SECURITY_ROLES as readonly unknown[]).includes(role))
    && (SECURITY_ROLES as readonly unknown[]).includes(value.role)
    && (OPERATIONAL_PROFILES as readonly unknown[]).includes(value.operationalProfile)
    && ['/overview', '/backups', '/3cx', '/onboarding'].includes(String(value.preferredLandingRoute))
    && ['/overview', '/backups'].includes(String(value.defaultRoute))
    && Array.isArray(value.dashboardOrder)
    && value.dashboardOrder.every((module) => (OPERATIONAL_MODULES as readonly unknown[]).includes(module));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
